import {
  createContext,
  type FunctionComponent,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import {
  I18nextProvider,
  useTranslation as useI18nTranslation,
} from 'react-i18next';

import { type I18n } from '../types/i18n.types';

import { CUSTOMER_MAP } from './AutoneTranslationContext.config';
import {
  type AppNamespace,
  type AutoneTranslationContextValue,
} from './AutoneTranslationContext.types';
import { getTranslationFunctions } from './utilities/translation-functions';
import { customerIdIsInCustomerMap } from './utilities/type-guards';

const AutoneTranslationContext =
  createContext<AutoneTranslationContextValue | null>(null);

interface AutoneTranslationContextProviderProps<
  NS extends AppNamespace = AppNamespace,
> extends PropsWithChildren {
  namespace: NS;
  customerId?: string;
}

const AutoneTranslationContextProvider = ({
  namespace,
  customerId,
  children,
}: AutoneTranslationContextProviderProps) => {
  const { i18n } = useI18nTranslation(namespace);

  const customerName =
    customerId && customerIdIsInCustomerMap(customerId)
      ? CUSTOMER_MAP[customerId]
      : null;

  const { t, common } = useMemo(
    () => getTranslationFunctions(i18n, namespace, customerName),
    [i18n.language, i18n, namespace, customerName],
  );

  const value: AutoneTranslationContextValue = {
    namespace,
    t,
    common,
    lang: i18n.language,
  };

  return (
    <AutoneTranslationContext.Provider value={value}>
      {children}
    </AutoneTranslationContext.Provider>
  );
};

interface AutoneTranslationProps extends AutoneTranslationContextProviderProps {
  i18n: I18n;
}

export const AutoneTranslation: FunctionComponent<AutoneTranslationProps> = ({
  namespace,
  children,
  i18n,
  customerId,
}) => {
  return (
    <I18nextProvider i18n={i18n}>
      <AutoneTranslationContextProvider
        namespace={namespace}
        customerId={customerId}
      >
        {children}
      </AutoneTranslationContextProvider>
    </I18nextProvider>
  );
};

export const useAutoneTranslation = <NS extends AppNamespace>() => {
  const context = useContext(AutoneTranslationContext);

  if (!context) {
    throw new Error(
      'useAutoneTranslationContext must be used within the AutoneTranslationContextProvider',
    );
  }

  return context as unknown as AutoneTranslationContextValue<NS>;
};
